import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DrillItem } from '../../context/DashboardContext';

// --- Drill-down variant (existing dashboard usage) ---
interface DrillBreadcrumbDrillProps {
  path:    DrillItem[];
  onUp:    (toIndex: number) => void;
  onReset: () => void;
  crumbs?: never;
}

// --- Page-level nav breadcrumb variant (new) ---
interface DrillBreadcrumbNavProps {
  crumbs:   { label: string; href?: string }[];
  path?:    never;
  onUp?:    never;
  onReset?: never;
}

type DrillBreadcrumbProps = DrillBreadcrumbDrillProps | DrillBreadcrumbNavProps;

export default function DrillBreadcrumb(props: DrillBreadcrumbProps) {
  const navigate = useNavigate();

  // --- Nav breadcrumb (page-level) ---
  if (props.crumbs !== undefined) {
    const { crumbs } = props;
    if (!crumbs.length) return null;

    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '5px 10px', marginBottom: 4,
        background: 'rgba(176,125,26,0.08)', borderRadius: 7, border: '1px solid #99F6E440',
      }}>
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <React.Fragment key={crumb.label + i}>
              <button
                onClick={() => {
                  if (!isLast && crumb.href) navigate(crumb.href);
                }}
                disabled={isLast}
                style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  background: 'none', border: 'none', padding: '1px 4px',
                  borderRadius: 4, cursor: isLast ? 'default' : 'pointer',
                  fontSize: 11, fontWeight: isLast ? 700 : 500,
                  color: isLast ? '#b07d1a' : '#5a5a6e',
                  textDecoration: !isLast ? 'underline' : 'none',
                }}
              >
                {i === 0 && <Home size={10} />}
                {crumb.label}
              </button>
              {!isLast && <ChevronRight size={11} color="#94A3B8" />}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // --- Drill-down breadcrumb (existing dashboard usage) ---
  const { path, onUp, onReset } = props;
  if (!path.length) return null;

  const drillCrumbs = [{ label: 'All', index: 0 }, ...path.map((p, i) => ({ label: p.label, index: i + 1 }))];

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '5px 10px', marginBottom: 4,
      background: 'rgba(176,125,26,0.08)', borderRadius: 7, border: '1px solid #99F6E440',
    }}>
      {drillCrumbs.map((crumb, i) => {
        const isLast = i === drillCrumbs.length - 1;
        return (
          <React.Fragment key={crumb.label + i}>
            <button
              onClick={() => {
                if (crumb.index === 0) onReset();
                else onUp(crumb.index);
              }}
              disabled={isLast}
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                background: 'none', border: 'none', padding: '1px 4px',
                borderRadius: 4, cursor: isLast ? 'default' : 'pointer',
                fontSize: 11, fontWeight: isLast ? 700 : 500,
                color: isLast ? '#b07d1a' : '#5a5a6e',
                textDecoration: !isLast ? 'underline' : 'none',
              }}>
              {crumb.index === 0 && <Home size={10} />}
              {crumb.label}
            </button>
            {!isLast && <ChevronRight size={11} color="#94A3B8" />}
          </React.Fragment>
        );
      })}
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>
        Click a bar to drill deeper · breadcrumb to go back
      </span>
    </div>
  );
}
